import React, { useEffect, useState } from 'react';
import { get, ref } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { updateTransaction } from '../services/transactionService';
import { scheduleMatterportScan } from '../services/transactionTasks';

// Matterport quick actions for the local assistant.
// "Schedule Matterport Scan": enter the appointment date and time, which sets the reminder task for the day before.
// "Text Client" on that task and on the reminder task: editable texts. Nothing sends automatically.
const INTRO_BODY = 'Hi {{client_first_name}}, this is {{assistant_name}}, assistant to {{agent_first_name}} with RESIDENCE | eXp Realty. I\'m reaching out to schedule the 3-D Matterport scan of your home. This is different from the photography and videography. The Matterport scan creates a 3-D virtual tour, so buyers can explore the home online, room to room, from anywhere. I would like to suggest {{suggested_when}} for the scan. It typically takes 1 to 2 hours, and I will be there to accompany the photographer. Does that work for you? If another time works better, just reply to this text and I\'ll get it scheduled.';

const REMINDER_BODY = 'Hi {{client_first_name}}, this is {{assistant_name}}, assistant to {{agent_first_name}} with RESIDENCE | eXp Realty. Just a reminder that the 3-D Matterport scan of your home is {{appointment}}. It typically takes 1 to 2 hours, and I will be there to accompany the photographer. If you need to reschedule, just reply to this text and I\'ll help.';

const fill = (text, data) => String(text || '').replace(/\{\{(\w+)\}\}/g, (m, k) => (data[k] == null ? '' : String(data[k])));

const longDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};
const shortTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};
const appointmentText = (deal) => {
  if (!deal?.matterportDate) return '';
  return `on ${longDate(deal.matterportDate)}${deal.matterportTime ? ` at ${shortTime(deal.matterportTime)}` : ''}`;
};

const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer' };
const btnOff = { ...btn, background: '#FFFFFF', color: '#B8962E', borderColor: '#D5D9E0', opacity: 0.5, cursor: 'not-allowed' };
const btnGhost = { ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' };
const input = { padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1A1A1A' };

export function MatterportScheduleAction({ task, transactionId, ownerUid, onSaved }) {
  const { user } = useAuth();
  const isSchedule = /^schedule matterport scan$/i.test(task.name || task.title || '');
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!isSchedule || !transactionId) return null;

  const save = async () => {
    try {
      setBusy(true);
      setError('');
      const name = user?.displayName || user?.email || 'Unknown';
      await updateTransaction(ownerUid || user?.uid, transactionId, { matterportDate: date, matterportTime: time });
      await scheduleMatterportScan(transactionId, date, time, name);
      setOpen(false);
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Could not save the Matterport appointment', err);
      setError('Could not save the appointment. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ margin: '6px 0' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={btn}>Enter scan date and time</button>
      ) : (
        <div style={{ marginTop: 4, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13, fontWeight: 600, gap: 4 }}>
              Scan date
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={input} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: 13, fontWeight: 600, gap: 4 }}>
              Scan time
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={input} />
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={save} disabled={!date || !time || busy} style={date && time && !busy ? btn : btnOff}>{busy ? 'Saving...' : 'Save appointment'}</button>
            <button type="button" onClick={() => setOpen(false)} disabled={busy} style={btnGhost}>Cancel</button>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: '#5B6270' }}>Saving sets a reminder task for the day before the scan.</div>
          {error && <div style={{ marginTop: 6, fontSize: 13, color: '#B00020' }}>{error}</div>}
        </div>
      )}
    </div>
  );
}

export default function MatterportClientText({ task, client, ownerUid, deal }) {
  const { user } = useAuth();
  const taskName = task.name || task.title || '';
  const isScheduleTask = /^schedule matterport scan$/i.test(taskName);
  const isReminderTask = /^text client: matterport scan reminder$/i.test(taskName);
  const [agentFirst, setAgentFirst] = useState('Kelly');
  const [open, setOpen] = useState(false);
  const [assistantName, setAssistantName] = useState('');
  const [when, setWhen] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!(isScheduleTask || isReminderTask) || !ownerUid) return;
    get(ref(database, `users/${ownerUid}/profile`))
      .then((snap) => { if (snap.exists() && snap.val().firstName) setAgentFirst(snap.val().firstName); })
      .catch((err) => console.error('Could not load agent', err));
  }, [isScheduleTask, isReminderTask, ownerUid]);

  if (!isScheduleTask && !isReminderTask) return null;

  const digits = (client?.phone || '').replace(/[^0-9+]/g, '');
  const clientFirst = client?.firstName || (client?.name || '').split(' ')[0] || '';
  const bodyTemplate = isReminderTask ? REMINDER_BODY : INTRO_BODY;

  const build = (whenText, nameText) => fill(bodyTemplate, {
    client_first_name: clientFirst,
    assistant_name: nameText || '[your name]',
    agent_first_name: agentFirst,
    suggested_when: whenText || '[suggested date and time]',
    appointment: whenText || '[appointment date and time]',
  });

  const toggle = () => {
    if (!open) {
      const defaultName = assistantName || (user?.displayName || '').split(' ')[0] || '';
      const defaultWhen = isReminderTask ? appointmentText(deal) : when;
      setAssistantName(defaultName);
      setWhen(defaultWhen);
      setMessage(build(defaultWhen, defaultName));
    }
    setOpen(!open);
  };

  const updateWhen = (value) => { setWhen(value); setMessage(build(value, assistantName)); };
  const updateName = (value) => { setAssistantName(value); setMessage(build(when, value)); };

  const ready = Boolean(digits && when.trim() && assistantName.trim());

  return (
    <div style={{ margin: '6px 0' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#1A1A1A' }}>Client: <strong>{clientFirst || 'not set'}</strong>{digits ? ` · ${client.phone}` : ''}</span>
        <button type="button" onClick={toggle} disabled={!digits} style={digits ? btn : btnOff}>Text Client</button>
        {!digits && <span style={{ fontSize: 12, color: '#5B6270' }}>No phone saved for the client yet.</span>}
      </div>
      {open && digits && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Your name</label>
          <input value={assistantName} onChange={(e) => updateName(e.target.value)} placeholder="e.g., Jane" style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{isReminderTask ? 'Appointment' : 'Suggested date and time'}</label>
          <input value={when} onChange={(e) => updateWhen(e.target.value)} placeholder="e.g., on Tuesday, October 27 at 10:00 AM" style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} style={{ ...input, width: '100%', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {ready ? (
              <a href={`sms:${digits}?body=${encodeURIComponent(message)}`} style={btn}>Send text</a>
            ) : (
              <span style={{ fontSize: 12, color: '#5B6270', alignSelf: 'center' }}>Add your name and the appointment to send.</span>
            )}
            <button type="button" onClick={() => setOpen(false)} style={btnGhost}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
